/**
 * The adversaries reading query and the per-account analysis-scope read/write. The analysis-scope
 * routes are tracked-account-scoped (`.../tracked-accounts/{id}/analysis-scope`), read from
 * `../../accounts/account/AnalysisScopeSelect.tsx` on the account screen, not only from this
 * feature's own view -- the schemas live here because this feature is what introduced the
 * ALL/RELATED vocabulary, matching `../../accounts/api/schemas.ts`'s own re-export convention for
 * a type another area owns.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiFailureError } from '../../../../lib/http/errors';
import { useApiClient } from '../../../session/client';
import type { ReadingWindow } from '../../accounts/api/queries';
import {
  analysisScopeStateSchema,
  candidateSchema,
  type AdversariesReading,
  type AnalysisScopeState,
  type AnalysisScopeWrite,
  type Candidate,
  type CandidateWrite,
} from './schemas';
import { GET_ROUTES, routePath } from '../../../../lib/http/routes';

function windowParams(window: ReadingWindow): URLSearchParams {
  return new URLSearchParams({ from: window.from, to: window.to });
}

export const ADVERSARIES_READING_QUERY_KEY = (campaignId: string, window: ReadingWindow) =>
  ['campaign', campaignId, 'adversaries-reading', window] as const;

export function useAdversariesReading(
  campaignId: string,
  window: ReadingWindow,
): UseQueryResult<AdversariesReading, ApiFailureError> {
  const client = useApiClient();
  return useQuery<AdversariesReading, ApiFailureError>({
    queryKey: ADVERSARIES_READING_QUERY_KEY(campaignId, window),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.adversariesReading, { campaignId }, windowParams(window)),
        GET_ROUTES.adversariesReading.schema,
      ),
    enabled: campaignId !== '',
  });
}

export const ANALYSIS_SCOPE_QUERY_KEY = (campaignId: string, trackedAccountId: string) =>
  ['campaign', campaignId, 'analysis-scope', trackedAccountId] as const;

function analysisScopePath(campaignId: string, trackedAccountId: string): string {
  return `/campaigns/${campaignId}/tracked-accounts/${trackedAccountId}/analysis-scope`;
}

export function useAnalysisScope(
  campaignId: string,
  trackedAccountId: string,
): UseQueryResult<AnalysisScopeState, ApiFailureError> {
  const client = useApiClient();
  return useQuery<AnalysisScopeState, ApiFailureError>({
    queryKey: ANALYSIS_SCOPE_QUERY_KEY(campaignId, trackedAccountId),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.analysisScope, { campaignId, trackedAccountId }),
        GET_ROUTES.analysisScope.schema,
      ),
    enabled: campaignId !== '' && trackedAccountId !== '',
  });
}

export function useWriteAnalysisScope(
  campaignId: string,
  trackedAccountId: string,
): UseMutationResult<AnalysisScopeState, ApiFailureError, AnalysisScopeWrite> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<AnalysisScopeState, ApiFailureError, AnalysisScopeWrite>({
    mutationFn: (body) =>
      client.putParsed(
        analysisScopePath(campaignId, trackedAccountId),
        body,
        analysisScopeStateSchema,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ANALYSIS_SCOPE_QUERY_KEY(campaignId, trackedAccountId),
      }),
  });
}

export const ACCOUNT_CANDIDATE_QUERY_KEY = (campaignId: string, trackedAccountId: string) =>
  ['campaign', campaignId, 'candidate', trackedAccountId] as const;

function candidatePath(campaignId: string, trackedAccountId: string): string {
  return `/campaigns/${campaignId}/tracked-accounts/${trackedAccountId}/candidate`;
}

export function useAccountCandidate(
  campaignId: string,
  trackedAccountId: string,
): UseQueryResult<Candidate, ApiFailureError> {
  const client = useApiClient();
  return useQuery<Candidate, ApiFailureError>({
    queryKey: ACCOUNT_CANDIDATE_QUERY_KEY(campaignId, trackedAccountId),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.accountCandidate, { campaignId, trackedAccountId }),
        GET_ROUTES.accountCandidate.schema,
      ),
    enabled: campaignId !== '' && trackedAccountId !== '',
  });
}

export function useWriteAccountCandidate(
  campaignId: string,
  trackedAccountId: string,
): UseMutationResult<Candidate, ApiFailureError, CandidateWrite> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Candidate, ApiFailureError, CandidateWrite>({
    mutationFn: (body) =>
      client.putParsed(candidatePath(campaignId, trackedAccountId), body, candidateSchema),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ACCOUNT_CANDIDATE_QUERY_KEY(campaignId, trackedAccountId),
      }),
  });
}
